import React, { useRef, useState, useCallback, useEffect } from 'react'

import {
    Box,
    Button
} from '@mui/material';

import { useNavigate, Link } from 'react-router-dom';

import { CommonIcons, icons } from '../../common/CommonIcons';
import CommonBreadcumbs from '../../common/CommonBreadcumbs'
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
import DataGridRowActionMenu from '../../common/DataGridRowActionMenu';

import { useAppContextFeatures } from "../../../context/AppContext";
import { useLocations, locationTypes, targetTypes } from '../../../hooks/api/useLocations';
import { useInventories, inventoriesTypes, dataTypes as inventoriesDataTypes } from "../../../hooks/api/useInventories";

import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { useTranslations } from "../../../hooks/useTranslations";

export default function ListLocations({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@location"] })

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { setSystemLoading } = useAppContextFeatures()
    const { list, remove } = useLocations({ locationType: locationTypes.location, targetType: targetTypes.location });
    const { list: listInventories, filterOperatorTypes } = useInventories({ inventoriesType: inventoriesTypes.inventory });

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnBooleanDisplay } = useDataGridParams('id')
    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const gridRef = React.createRef();

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    const fetchData = async (props) => {

        // setSystemLoading(true)
        resetDataGridTableSelection();

        try {

            // add in data types
            props.dataTypes = ["rack_slot", "zone_count", "rack_count"]

            const result = await list(props)

            return {
                data: result?.data?.locations,
                count: result?.data?.total
            }

        }

        catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }

    }

    useEffect(() => {
        defineColumns([
            {
                name: 'location_code',
                header: t('code'),
                renderValue: ({ value, cellProps }) => {

                    let path = getRoute(`settings/location/update/${cellProps.data.id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'location_name',
                header: t('name'),
                renderValue: ({ value, cellProps }) => {

                    return value

                    // let path = getRoute(`settings/location/update/${cellProps.data.id}`);

                    // return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'state_name', header: t('state')
            },
            {
                name: 'country_name', header: t('country')
            },
            {
                name: 'status',
                header: t('status'),
                renderValue: ({ cellProps, value }) => getRenderColumnBooleanDisplay(value, [t("enabled"), t("disabled")], ["primary", "default"], "")
            },
            // { 
            //     name: 'is_default', header: 'Default',
            //     renderValue: ({ cellProps, value }) => getRenderColumnBooleanDisplay(value, ["YES", "-"], ["primary", "default"], "-")
            // },
            {
                name: 'rack_count',
                header: t('racks'),
                sortable: false,
                renderValue: ({ value, cellProps }) => {

                    let displayValue = parseInt(value, 10) > 0 ? `${value} ${t('racks')}` : ``

                    return (
                        <>{displayValue}</>
                    )

                },
            },
            {
                name: 'zone_count',
                header: t('zones'),
                sortable: false,
                renderValue: ({ value, cellProps }) => {

                    let displayValue = parseInt(value, 10) > 0 ? `${value} ${t('zones')}` : ``

                    return (
                        <>{displayValue}</>
                    )

                },
            },
        ])
    })

    // const filterValue = 
    //     [
    //         // { name: 'action', operator: 'eq', type: 'string', value: '' },
    //         { name: 'location_name', operator: 'eq', type: 'string', value: '' },
    //         { name: 'address_1', operator: 'eq', type: 'string', value: '' },
    //         { name: 'address_2', operator: 'eq', type: 'string', value: '' },
    //         { name: 'state_name', operator: 'eq', type: 'string', value: '' },
    //         { name: 'country_name', operator: 'eq', type: 'string', value: '' },
    //         // { name: 'email', operator: 'eq', type: 'string', value: '' },
    //         // { name: 'company_name', operator: 'eq', type: 'string', value: '' },
    //         //   { name: 'ax_createdatetime', operator: 'eq', type: 'string', value: '' },
    //     ];

    const getInventories = async ({ locationId }) => {

        setSystemLoading(true)

        try {

            let filterValue = [];

            if (locationId) {
                filterValue.push({
                    name: "location_id",
                    operator: filterOperatorTypes.eq,
                    value: locationId
                })
            }

            let props = {
                limit: -1,
                skip: 0,
                sortInfo: {
                    id: "id",
                    dir: 0,
                },
                filterValue
            };

            props.dataTypes = [inventoriesDataTypes.groupSlotBatch]

            const { data } = await listInventories(props)

            setSystemLoading(false)

            return data?.inventories ? data.inventories : []

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

        return true;

    }

    const checkAllLocationInventories = async ({ selectedIds }) => {

        let allowDelete = true

        // await selectedIds.forEach(async(e) => {
        //     const res = await getInventories({ locationId: e })
        //     console.log('res', res)
        //     if(res.length > 0){
        //         allowDelete = false
        //     }
        // })

        await Promise.all(selectedIds.map(async (id) => {
            const res = await getInventories({ locationId: id })
            // console.log('res', res)
            if (res.length > 0) {
                allowDelete = false
            }
        }));

        return allowDelete

    }

    const handleDelete = async () => {

        try {
            // limit only selected 3 at one time
            if (selectedIds.length > 3) throw new Error(`Max selection for (3) Only`)

            let allowDelete = await checkAllLocationInventories({ selectedIds })

            // console.log('allowDelete', JSON.stringify(allowDelete))

            if (allowDelete === false) {
                promptMessage(false, `Selected of the locations entity utilized`)
            } else {
                let result = await remove({ ids: selectedIds })

                promptMessage(true, result?.message)
                setRefreshDataGrid(val => val + 1)
            }


        } catch (error) {
            promptMessage(false, `Action Failed : ${error?.message}`)
        }

    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.locations })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("locations")}
                breadcrumbs={[
                    {
                        displayText: t("locations"),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={[
                    {
                        // displayText: "New Location",
                        displayIconName: icons.plus,
                        onClick: () => {

                            navigate(getRoute("settings/location/new"));

                        }
                    },
                ]}
                actionOnClickRemove={handleDelete}
                actionMenus={[
                    // {
                    //     displayText: "Remove",
                    //     onClick: handleDelete
                    // }
                ]}
            />

            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn: true,
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        // console.log('items selected : ', items)
                        setSelectedIds(
                            items
                        )
                    }
                }}

            />

        </Box>
    )
}
